import React, { useState, useEffect, useRef } from 'react'
import moment from 'moment'
import 'moment/locale/he'
import {getAdminToken} from '../auth/_requests'
moment.locale('he')

// Helper to get auth headers
function getAuthHeaders(): HeadersInit {
  const token = getAdminToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

interface CronStatus {
  enabled: boolean
  cronTime: string
  lastRun: string | null
  lastRunResult: any | null
}

interface JobProgress {
  status: 'idle' | 'running' | 'completed' | 'error' | 'stopped'
  progress: {
    current: number
    total: number
    percentage: number
  }
  currentAccount?: {
    id: string
    name: string
    email: string
  }
  results: {
    success: number
    failed: number
    completed: Array<{
      accountId: string
      accountName: string
      success: boolean
      slotsCreated?: number
      postsScheduled?: number
      error?: string
    }>
  }
  startedAt?: string
  completedAt?: string
  targetDate?: string
  targetDayName?: string
  error?: string
}

const API_URL = process.env.REACT_APP_API_URL

// Light theme colors
const colors = {
  bgPrimary: '#f5f8fa',
  bgSecondary: '#ffffff',
  bgTertiary: '#e8ecef',
  bgHover: '#e1e5ea',
  textPrimary: '#1e1e2d',
  textSecondary: '#5e6278',
  textMuted: '#a1a5b7',
  accentPrimary: '#3699ff',
  accentSuccess: '#1bc5bd',
  accentWarning: '#ffa800',
  accentDanger: '#f64e60',
  borderColor: 'rgba(0, 0, 0, 0.08)',
}

const SchedulerPage: React.FC = () => {
  const [cronStatus, setCronStatus] = useState<CronStatus | null>(null)
  const [jobProgress, setJobProgress] = useState<JobProgress | null>(null)
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState(false)
  const [starting, setStarting] = useState(false)
  const [hoveredCard, setHoveredCard] = useState<number | null>(null)
  const eventSourceRef = useRef<EventSource | null>(null)
  const currentJobIdRef = useRef<string | null>(null)
  const [resettingLowGroups, setResettingLowGroups] = useState(false)
  const [resetLowGroupsResult, setResetLowGroupsResult] = useState<string | null>(null)
  const [resettingStaleGroups, setResettingStaleGroups] = useState(false)
  const [resetStaleGroupsResult, setResetStaleGroupsResult] = useState<string | null>(null)

  useEffect(() => {
    loadCronStatus()
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  const loadCronStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/schedule/cron/status`, {
        credentials: 'include',
        headers: getAuthHeaders()
      })
      if (response.ok) {
        const data = await response.json()
        setCronStatus(data)
      }
    } catch (error) {
      console.error('Failed to load cron status:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleCron = async () => {
    if (!cronStatus) return
    setToggling(true)
    try {
      const response = await fetch(`${API_URL}/admin/schedule/cron/toggle`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ enabled: !cronStatus.enabled })
      })
      if (response.ok) {
        const data = await response.json()
        setCronStatus(data)
      }
    } catch (error) {
      console.error('Failed to toggle cron:', error)
    } finally {
      setToggling(false)
    }
  }

  const startReschedule = async () => {
    setStarting(true)
    try {
      const response = await fetch(`${API_URL}/admin/schedule/reschedule-all`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      })
      if (response.ok) {
        const data = await response.json()
        currentJobIdRef.current = data.jobId
        connectToStream(data.jobId)
      }
    } catch (error) {
      console.error('Failed to start reschedule:', error)
      setStarting(false)
    }
  }

  const connectToStream = (jobId: string, retryCount = 0) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    // EventSource doesn't support custom headers, so pass token as query param
    const token = getAdminToken()
    const tokenParam = token ? `&token=${encodeURIComponent(token)}` : ''
    const eventSource = new EventSource(`${API_URL}/admin/schedule/reschedule-all/stream?jobId=${jobId}${tokenParam}`, {
      withCredentials: true
    })
    eventSourceRef.current = eventSource

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.status === 'heartbeat') return
        setJobProgress(data)
        setStarting(false)

        if (data.status === 'completed' || data.status === 'error' || data.status === 'stopped') {
          eventSource.close()
          eventSourceRef.current = null
          loadCronStatus() // Refresh cron status to get latest run info
        }
      } catch (e) {
        console.error('Failed to parse SSE data:', e)
      }
    }

    eventSource.onerror = () => {
      eventSource.close()
      eventSourceRef.current = null

      // Reconnect up to 10 times with increasing delay
      if (retryCount < 10 && currentJobIdRef.current === jobId) {
        const delay = Math.min(2000 * (retryCount + 1), 10000)
        console.log(`SSE disconnected, reconnecting in ${delay}ms (attempt ${retryCount + 1})`)
        setTimeout(() => {
          if (currentJobIdRef.current === jobId) {
            connectToStream(jobId, retryCount + 1)
          }
        }, delay)
      } else {
        setStarting(false)
        // Try to fetch final status via polling
        fetch(`${API_URL}/admin/schedule/reschedule-all/status?jobId=${jobId}`, {
          credentials: 'include',
          headers: getAuthHeaders()
        })
          .then(res => res.json())
          .then(data => {
            if (data.status && data.status !== 'idle') {
              setJobProgress(data)
            }
          })
          .catch(() => {})
      }
    }
  }

  const runNow = async () => {
    setStarting(true)
    try {
      const response = await fetch(`${API_URL}/admin/schedule/cron/run-now`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      })
      if (response.ok) {
        const data = await response.json()
        currentJobIdRef.current = data.jobId
        connectToStream(data.jobId)
      }
    } catch (error) {
      console.error('Failed to run now:', error)
      setStarting(false)
    }
  }

  const runTomorrow = async () => {
    setStarting(true)
    try {
      const response = await fetch(`${API_URL}/admin/schedule/cron/run-tomorrow`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      })
      if (response.ok) {
        const data = await response.json()
        if (data.isShabbat) {
          alert(data.message)
          setStarting(false)
          return
        }
        currentJobIdRef.current = data.jobId
        connectToStream(data.jobId)
      }
    } catch (error) {
      console.error('Failed to run tomorrow:', error)
      setStarting(false)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'לא רץ עדיין'
    return moment(dateStr).format('DD/MM/YYYY HH:mm:ss')
  }

  const isRunning = jobProgress?.status === 'running' || starting

  const statsCards = [
    {
      label: 'סטטוס קרון',
      value: cronStatus?.enabled ? 'פעיל' : 'מושבת',
      icon: cronStatus?.enabled ? 'bi-play-circle-fill' : 'bi-pause-circle-fill',
      gradient: cronStatus?.enabled
        ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
        : 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)',
    },
    {
      label: 'שעת הפעלה',
      value: cronStatus?.cronTime || '07:00',
      icon: 'bi-clock-fill',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      label: 'הרצה אחרונה',
      value: cronStatus?.lastRun ? moment(cronStatus.lastRun).fromNow() : 'לא רץ',
      icon: 'bi-calendar-check-fill',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
    {
      label: 'תוצאה אחרונה',
      value: cronStatus?.lastRunResult?.success
        ? `${cronStatus.lastRunResult.totalSlots || 0} פוסטים`
        : (cronStatus?.lastRunResult ? 'שגיאה' : '—'),
      icon: cronStatus?.lastRunResult?.success ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill',
      gradient: cronStatus?.lastRunResult?.success
        ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
        : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
  ]

  const handleResetLowGroups = async () => {
    setResettingLowGroups(true)
    setResetLowGroupsResult(null)
    try {
      const token = getAdminToken()
      const response = await fetch(`${API_URL}/admin/schedule/reset-get-groups-low`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeaders() }
      })
      if (response.ok) {
        const data = await response.json()
        setResetLowGroupsResult(`אופסו ${data.resetCount || 0} פרופילים`)
        setTimeout(() => setResetLowGroupsResult(null), 5000)
      }
    } catch (error) {
      console.error('Failed to reset low groups:', error)
    } finally {
      setResettingLowGroups(false)
    }
  }

  const handleResetStaleGroups = async () => {
    setResettingStaleGroups(true)
    setResetStaleGroupsResult(null)
    try {
      const token = getAdminToken()
      const response = await fetch(`${API_URL}/admin/schedule/reset-get-groups-stale`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeaders() }
      })
      if (response.ok) {
        const data = await response.json()
        setResetStaleGroupsResult(`אופסו ${data.resetCount || 0} פרופילים`)
        setTimeout(() => setResetStaleGroupsResult(null), 5000)
      }
    } catch (error) {
      console.error('Failed to reset stale groups:', error)
    } finally {
      setResettingStaleGroups(false)
    }
  }

  return (
    <div style={{ padding: 0 }}>
      {/* Page Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: 700,
          color: colors.textPrimary,
          margin: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          display: 'inline-block'
        }}>
          תזמון קמפיינים
        </h1>
        <p style={{ color: colors.textSecondary, marginTop: '8px', fontSize: '14px' }}>
          ניהול תזמון אוטומטי של כל הקמפיינים עבור כל החשבונות
        </p>
      </div>

      {loading ? (
        <div style={{
          padding: '80px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: `3px solid ${colors.borderColor}`,
            borderTopColor: '#667eea',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}></div>
          <div style={{ color: colors.textSecondary, fontSize: '14px' }}>טוען...</div>
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '20px',
            marginBottom: '32px',
          }}>
            {statsCards.map((stat, i) => (
              <div
                key={i}
                onMouseEnter={() => setHoveredCard(i)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  position: 'relative',
                  background: hoveredCard === i
                    ? colors.bgHover
                    : colors.bgSecondary,
                  borderRadius: '20px',
                  padding: '24px',
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  transform: hoveredCard === i ? 'translateY(-8px) scale(1.02)' : 'none',
                  boxShadow: hoveredCard === i
                    ? '0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px ' + colors.borderColor
                    : '0 4px 20px rgba(0,0,0,0.08)',
                  border: `1px solid ${colors.borderColor}`,
                  overflow: 'hidden',
                }}
              >
                {/* Gradient Glow Effect */}
                <div style={{
                  position: 'absolute',
                  top: '-50%',
                  right: '-50%',
                  width: '100%',
                  height: '100%',
                  background: stat.gradient,
                  opacity: hoveredCard === i ? 0.15 : 0.05,
                  borderRadius: '50%',
                  filter: 'blur(60px)',
                  transition: 'opacity 0.4s ease',
                  pointerEvents: 'none',
                }} />

                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '16px',
                    background: stat.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  }}>
                    <i className={`bi ${stat.icon}`} style={{
                      fontSize: '24px',
                      color: '#fff',
                    }}></i>
                  </div>
                  <div style={{
                    fontSize: '24px',
                    fontWeight: 800,
                    color: colors.textPrimary,
                    lineHeight: 1,
                  }}>
                    {stat.value}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: colors.textMuted,
                    marginTop: '8px',
                    fontWeight: 500,
                  }}>
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Actions Section */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            marginBottom: '32px',
          }}>
            {/* Cron Control Card */}
            <div style={{
              background: colors.bgSecondary,
              borderRadius: '20px',
              border: `1px solid ${colors.borderColor}`,
              padding: '28px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            }}>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: colors.textPrimary, fontSize: '18px', fontWeight: 600, margin: 0 }}>
                  <i className="bi bi-gear" style={{ marginLeft: '10px' }}></i>
                  הגדרות קרון
                </h3>
                <p style={{ color: colors.textSecondary, fontSize: '13px', marginTop: '8px' }}>
                  הקרון רץ כל יום בשעה 07:00 לפי שעון ישראל
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={toggleCron}
                  disabled={toggling}
                  style={{
                    flex: 1,
                    padding: '14px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    background: cronStatus?.enabled
                      ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)'
                      : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: toggling ? 'not-allowed' : 'pointer',
                    opacity: toggling ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease',
                    boxShadow: cronStatus?.enabled
                      ? '0 8px 24px rgba(255, 107, 107, 0.3)'
                      : '0 8px 24px rgba(17, 153, 142, 0.3)',
                  }}
                >
                  {toggling ? (
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                    }}></div>
                  ) : (
                    <i className={`bi ${cronStatus?.enabled ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
                  )}
                  {cronStatus?.enabled ? 'השבת קרון' : 'הפעל קרון'}
                </button>

                <button
                  onClick={runNow}
                  disabled={isRunning}
                  style={{
                    flex: 1,
                    padding: '14px 24px',
                    borderRadius: '12px',
                    border: '2px solid rgba(102, 126, 234, 0.5)',
                    background: 'rgba(102, 126, 234, 0.1)',
                    color: '#667eea',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: isRunning ? 'not-allowed' : 'pointer',
                    opacity: isRunning ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <i className="bi bi-lightning-charge"></i>
                  הרץ עכשיו
                </button>
              </div>
            </div>

            {/* Manual Reschedule Card */}
            <div style={{
              background: colors.bgSecondary,
              borderRadius: '20px',
              border: `1px solid ${colors.borderColor}`,
              padding: '28px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            }}>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: colors.textPrimary, fontSize: '18px', fontWeight: 600, margin: 0 }}>
                  <i className="bi bi-arrow-repeat" style={{ marginLeft: '10px' }}></i>
                  תזמון מחדש ידני
                </h3>
                <p style={{ color: colors.textSecondary, fontSize: '13px', marginTop: '8px' }}>
                  הפעל תזמון מחדש של כל הקמפיינים לכל החשבונות הפעילים
                </p>
              </div>

              <button
                onClick={startReschedule}
                disabled={isRunning}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  borderRadius: '12px',
                  border: 'none',
                  background: isRunning
                    ? colors.bgTertiary
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: isRunning ? colors.textMuted : '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: isRunning ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  boxShadow: isRunning ? 'none' : '0 8px 24px rgba(102, 126, 234, 0.4)',
                }}
              >
                {isRunning ? (
                  <>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                    }}></div>
                    מתזמן...
                  </>
                ) : (
                  <>
                    <i className="bi bi-calendar-plus"></i>
                    תזמן מחדש את כל החשבונות
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Test Tomorrow Section */}
          <div style={{
            background: colors.bgSecondary,
            borderRadius: '20px',
            border: `2px dashed ${colors.accentWarning}`,
            padding: '28px',
            marginBottom: '32px',
            boxShadow: '0 4px 20px rgba(255, 168, 0, 0.15)',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #ffa800 0%, #ff6b00 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <i className="bi bi-calendar2-week" style={{ fontSize: '24px', color: '#fff' }}></i>
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ color: colors.textPrimary, fontSize: '18px', fontWeight: 600, margin: '0 0 8px 0' }}>
                  בדיקת תזמון למחר
                </h3>
                <p style={{ color: colors.textSecondary, fontSize: '13px', margin: '0 0 16px 0' }}>
                  הרץ תזמון לכל החשבונות עבור מחר כדי לוודא שהכל עובד כמו שצריך.
                  זה ירוץ בתהליך נפרד ותוכל לראות התקדמות בזמן אמת.
                </p>
                <button
                  onClick={runTomorrow}
                  disabled={isRunning}
                  style={{
                    padding: '14px 32px',
                    borderRadius: '12px',
                    border: 'none',
                    background: isRunning
                      ? colors.bgTertiary
                      : 'linear-gradient(135deg, #ffa800 0%, #ff6b00 100%)',
                    color: isRunning ? colors.textMuted : '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: isRunning ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease',
                    boxShadow: isRunning ? 'none' : '0 8px 24px rgba(255, 168, 0, 0.4)',
                  }}
                >
                  {isRunning ? (
                    <>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTopColor: '#fff',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                      }}></div>
                      מתזמן...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-calendar-check"></i>
                      תזמן למחר (בדיקה)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Reset Get Groups Section */}
          <div style={{
            background: colors.bgSecondary,
            borderRadius: '20px',
            border: `1px solid ${colors.borderColor}`,
            padding: '28px',
            marginBottom: '32px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          }}>
            <h3 style={{ color: colors.textPrimary, fontSize: '18px', fontWeight: 600, margin: '0 0 16px 0' }}>
              <i className="bi bi-people" style={{ marginLeft: '10px' }}></i>
              איפוס Get Groups
            </h3>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  onClick={handleResetLowGroups}
                  disabled={resettingLowGroups}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    background: resettingLowGroups ? colors.bgTertiary : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: resettingLowGroups ? colors.textMuted : '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: resettingLowGroups ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {resettingLowGroups ? (
                    <div style={{ width: '18px', height: '18px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
                  ) : (
                    <i className="bi bi-arrow-counterclockwise"></i>
                  )}
                  פחות מ-10 קבוצות
                </button>
                {resetLowGroupsResult && (
                  <span style={{ color: colors.accentSuccess, fontSize: '13px', fontWeight: 600 }}>{resetLowGroupsResult}</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  onClick={handleResetStaleGroups}
                  disabled={resettingStaleGroups}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    background: resettingStaleGroups ? colors.bgTertiary : 'linear-gradient(135deg, #ffa800 0%, #ff6b00 100%)',
                    color: resettingStaleGroups ? colors.textMuted : '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: resettingStaleGroups ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {resettingStaleGroups ? (
                    <div style={{ width: '18px', height: '18px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
                  ) : (
                    <i className="bi bi-arrow-counterclockwise"></i>
                  )}
                  עברו 3 ימים
                </button>
                {resetStaleGroupsResult && (
                  <span style={{ color: colors.accentSuccess, fontSize: '13px', fontWeight: 600 }}>{resetStaleGroupsResult}</span>
                )}
              </div>
            </div>
          </div>

          {/* Progress Section */}
          {jobProgress && (
            <div style={{
              background: colors.bgSecondary,
              borderRadius: '20px',
              border: `1px solid ${colors.borderColor}`,
              overflow: 'hidden',
              marginBottom: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            }}>
              {/* Progress Header */}
              <div style={{
                padding: '20px 28px',
                borderBottom: `1px solid ${colors.borderColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '12px',
                    background: jobProgress.status === 'running'
                      ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                      : jobProgress.status === 'completed'
                      ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                      : 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <i className={`bi ${
                      jobProgress.status === 'running'
                        ? 'bi-arrow-repeat'
                        : jobProgress.status === 'completed'
                        ? 'bi-check-lg'
                        : 'bi-x-lg'
                    }`} style={{
                      color: '#fff',
                      fontSize: '18px',
                      animation: jobProgress.status === 'running' ? 'spin 1s linear infinite' : 'none',
                    }}></i>
                  </div>
                  <div>
                    <div style={{ color: colors.textPrimary, fontSize: '16px', fontWeight: 600 }}>
                      {jobProgress.status === 'running' && (jobProgress.targetDate ? 'תזמון למחר בתהליך...' : 'תזמון בתהליך...')}
                      {jobProgress.status === 'completed' && (jobProgress.targetDate ? 'התזמון למחר הושלם!' : 'התזמון הושלם!')}
                      {jobProgress.status === 'error' && 'שגיאה בתזמון'}
                      {jobProgress.status === 'stopped' && 'התזמון הופסק'}
                    </div>
                    {jobProgress.targetDate && (
                      <div style={{ color: colors.accentWarning, fontSize: '12px', marginTop: '2px', fontWeight: 500 }}>
                        <i className="bi bi-calendar2-week" style={{ marginLeft: '4px' }}></i>
                        תאריך יעד: {jobProgress.targetDate}
                      </div>
                    )}
                    {jobProgress.currentAccount && jobProgress.status === 'running' && (
                      <div style={{ color: colors.textSecondary, fontSize: '13px', marginTop: '2px' }}>
                        מעבד: {jobProgress.currentAccount.name || jobProgress.currentAccount.email}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ color: colors.textPrimary, fontSize: '24px', fontWeight: 700 }}>
                    {jobProgress.progress.percentage}%
                  </div>
                  <div style={{ color: colors.textSecondary, fontSize: '12px' }}>
                    {jobProgress.progress.current} / {jobProgress.progress.total} חשבונות
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div style={{ padding: '0 28px', marginTop: '-1px' }}>
                <div style={{
                  height: '6px',
                  background: colors.bgTertiary,
                  borderRadius: '3px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${jobProgress.progress.percentage}%`,
                    height: '100%',
                    background: jobProgress.status === 'running'
                      ? 'linear-gradient(90deg, #667eea, #764ba2)'
                      : jobProgress.status === 'completed'
                      ? 'linear-gradient(90deg, #11998e, #38ef7d)'
                      : 'linear-gradient(90deg, #ff6b6b, #ee5a5a)',
                    transition: 'width 0.5s ease',
                    borderRadius: '3px',
                  }}></div>
                </div>
              </div>

              {/* Results Summary */}
              <div style={{
                padding: '20px 28px',
                display: 'flex',
                gap: '24px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#38ef7d',
                  }}></div>
                  <span style={{ color: colors.textSecondary, fontSize: '13px' }}>הצליחו:</span>
                  <span style={{ color: '#38ef7d', fontSize: '14px', fontWeight: 600 }}>
                    {jobProgress.results.success}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#ff6b6b',
                  }}></div>
                  <span style={{ color: colors.textSecondary, fontSize: '13px' }}>נכשלו:</span>
                  <span style={{ color: '#ff6b6b', fontSize: '14px', fontWeight: 600 }}>
                    {jobProgress.results.failed}
                  </span>
                </div>
              </div>

              {/* Completed Accounts List */}
              {jobProgress.results.completed.length > 0 && (
                <div style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  borderTop: `1px solid ${colors.borderColor}`,
                }}>
                  {jobProgress.results.completed.slice(-10).reverse().map((result, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '12px 28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${colors.borderColor}`,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '6px',
                          background: result.success !== false
                            ? 'rgba(56, 239, 125, 0.1)'
                            : 'rgba(255, 107, 107, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <i className={`bi ${result.success !== false ? 'bi-check' : 'bi-x'}`} style={{
                            color: result.success !== false ? '#38ef7d' : '#ff6b6b',
                            fontSize: '14px',
                          }}></i>
                        </div>
                        <span style={{ color: colors.textPrimary, fontSize: '13px' }}>
                          {result.accountName}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                        {result.success !== false
                          ? `${result.postsScheduled || result.slotsCreated || 0} פוסטים`
                          : result.error || 'שגיאה'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Last Run Details */}
          {cronStatus?.lastRunResult && !jobProgress && (
            <div style={{
              background: colors.bgSecondary,
              borderRadius: '20px',
              border: `1px solid ${colors.borderColor}`,
              padding: '28px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            }}>
              <h3 style={{ color: colors.textPrimary, fontSize: '18px', fontWeight: 600, margin: '0 0 20px 0' }}>
                <i className="bi bi-clock-history" style={{ marginLeft: '10px' }}></i>
                פרטי הרצה אחרונה
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                <div>
                  <div style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px' }}>תאריך</div>
                  <div style={{ color: colors.textPrimary, fontSize: '14px', fontWeight: 500 }}>
                    {formatDate(cronStatus.lastRun)}
                  </div>
                </div>
                <div>
                  <div style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px' }}>חשבונות</div>
                  <div style={{ color: colors.textPrimary, fontSize: '14px', fontWeight: 500 }}>
                    {cronStatus.lastRunResult.totalAccounts || 0}
                  </div>
                </div>
                <div>
                  <div style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px' }}>פוסטים שנוצרו</div>
                  <div style={{ color: '#38ef7d', fontSize: '14px', fontWeight: 500 }}>
                    {cronStatus.lastRunResult.totalSlots || 0}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

export default SchedulerPage
